import React, { useMemo, useState } from "react";
import AuthInput from "../../../components/Input/AuthInput";
import CustomPagination from "../../../components/TablePagination/TablePagination";
import { FiChevronDown, FiInbox, FiPackage } from "react-icons/fi";
import {
  buildDateItemSalesSummary,
  filterDateItemSalesSummary,
  formatSaleDateLabel,
  sumDateItemSales,
} from "../../../utils/reportItemSalesSummary";

const formatRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const formatQty = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 2 });

const DateItemSalesCard = ({ day, expanded, onToggle }) => (
  <div className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex flex-wrap items-start justify-between gap-3 p-4 text-left hover:bg-subtle/80 transition-colors"
    >
      <div className="min-w-0 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-brandSoft flex items-center justify-center shrink-0">
          <FiPackage className="text-main" size={18} />
        </div>
        <div>
          <p className="text-sm font-bold text-ink">
            {formatSaleDateLabel(day.date)}
          </p>
          <p className="text-xs text-muted">
            {day.invoice_count} invoice{day.invoice_count !== 1 ? "s" : ""} ·{" "}
            {day.items.length} item{day.items.length !== 1 ? "s" : ""} · Qty{" "}
            {formatQty(day.total_qty)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wide text-muted font-semibold">
            Sale amount
          </p>
          <p className="text-sm font-bold text-main tabular-nums">
            Rs {formatRs(day.total_amount)}
          </p>
        </div>
        <FiChevronDown
          className={`text-muted transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </div>
    </button>

    {expanded && (
      <div className="border-t border-line px-4 pb-4 pt-3">
        {day.items.length === 0 ? (
          <p className="text-sm text-muted py-2">No line items on this day.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="w-full text-left text-sm">
              <thead className="bg-subtle text-xs uppercase text-muted">
                <tr>
                  <th className="px-3 py-2 font-bold">Item</th>
                  <th className="px-3 py-2 font-bold">Unit</th>
                  <th className="px-3 py-2 font-bold text-right">Qty sold</th>
                  <th className="px-3 py-2 font-bold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {day.items.map((item) => (
                  <tr
                    key={`${day.date}-${item.itemId}-${item.name}`}
                    className="border-t border-line"
                  >
                    <td className="px-3 py-2 font-semibold text-ink">
                      {item.name}
                    </td>
                    <td className="px-3 py-2 text-muted">{item.unit}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-ink">
                      {formatQty(item.qty)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-main">
                      Rs {formatRs(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-line bg-subtle/60">
                  <td
                    className="px-3 py-2 font-bold text-ink"
                    colSpan={2}
                  >
                    Day total
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">
                    {formatQty(day.total_qty)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-main">
                    Rs {formatRs(day.total_amount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    )}
  </div>
);

const ReportItemSalesSummary = ({
  salesInvoices = [],
  page = 0,
  rowsPerPage = 100,
  onPageChange,
  onRowsPerPageChange,
  searchText = "",
  setSearchText,
}) => {
  const [expandedKey, setExpandedKey] = useState(null);

  const dayGroups = useMemo(
    () => buildDateItemSalesSummary(salesInvoices),
    [salesInvoices]
  );

  const filtered = useMemo(
    () => filterDateItemSalesSummary(dayGroups, searchText),
    [dayGroups, searchText]
  );

  const grand = useMemo(() => sumDateItemSales(dayGroups), [dayGroups]);

  if (!salesInvoices.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
        <div className="w-16 h-16 rounded-2xl bg-brandSoft flex items-center justify-center mb-4">
          <FiInbox className="text-main" size={28} />
        </div>
        <p className="font-bold text-inkSecondary text-lg">No item sales</p>
        <p className="text-sm text-muted mt-1 text-center max-w-md">
          No sales invoices found for this date range.
        </p>
      </div>
    );
  }

  const paged = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="flex flex-col w-full gap-3">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-main/20 bg-brandSoft px-4 py-3">
        <p className="text-sm font-semibold text-main">
          Item sales by date — customer + guest (qty &amp; amount)
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm tabular-nums font-bold text-main">
          <span>
            {grand.days} day{grand.days !== 1 ? "s" : ""}
          </span>
          <span>Qty {formatQty(grand.qty)}</span>
          <span>Rs {formatRs(grand.amount)}</span>
        </div>
      </div>

      <div className="flex flex-col justify-end items-end gap-2 mb-1">
        <AuthInput
          Value={searchText}
          setValue={setSearchText}
          placeholder="Search item name or date"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="w-full text-center text-muted py-8 text-sm">
          No matches for your search.
        </div>
      ) : (
        paged.map((day) => (
          <DateItemSalesCard
            key={day.date}
            day={day}
            expanded={expandedKey === day.date}
            onToggle={() =>
              setExpandedKey((prev) => (prev === day.date ? null : day.date))
            }
          />
        ))
      )}

      {filtered.length > 0 && (
        <CustomPagination
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          RowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </div>
  );
};

export default ReportItemSalesSummary;
